import { useLanguage } from "../contexts/LanguageContext";
import FoundationIcon from '@mui/icons-material/Foundation';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import LineAxisIcon from '@mui/icons-material/LineAxis';
import { useEffect, useRef } from "react";

export default function StructureService() {
    const { t } = useLanguage();
    const isMobile = window.innerWidth < 768;
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const descriptionRef = useRef<HTMLParagraphElement>(null);
    const imageRef = useRef<HTMLDivElement>(null);
    const cardsRef = useRef<HTMLDivElement>(null);
    const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        // Animate title
                        if (entry.target === titleRef.current) {
                            entry.target.classList.add('animate-fade-in-up');
                        }
                        // Animate description
                        if (entry.target === descriptionRef.current) {
                            setTimeout(() => {
                                entry.target.classList.add('animate-fade-in-up');
                            }, 200);
                        }
                        // Animate image
                        if (entry.target === imageRef.current) {
                            setTimeout(() => {
                                entry.target.classList.add('animate-fade-in-up');
                            }, 400);
                        }
                        // Animate cards container
                        if (entry.target === cardsRef.current) {
                            setTimeout(() => {
                                entry.target.classList.add('animate-fade-in-up');
                            }, 600);
                        }
                        // Animate individual cards with staggered delay
                        if (cardRefs.current.includes(entry.target as HTMLDivElement)) {
                            const index = cardRefs.current.indexOf(entry.target as HTMLDivElement);
                            setTimeout(() => {
                                entry.target.classList.add('animate-fade-in-up');
                            }, 800 + (index * 150)); // 800ms base delay + 150ms per card
                        }
                    }
                });
            },
            { threshold: 0.1 }
        );

        // Observe all elements
        if (titleRef.current) observer.observe(titleRef.current);
        if (descriptionRef.current) observer.observe(descriptionRef.current);
        if (imageRef.current) observer.observe(imageRef.current);
        if (cardsRef.current) observer.observe(cardsRef.current);
        cardRefs.current.forEach((card) => {
            if (card) observer.observe(card);
        });

        return () => observer.disconnect();
    }, []);

    return (
        <section ref={sectionRef} id="structureService" className=" relative overflow-hidden min-h-[70vh] md:h-[100vh] bg-[#244473]/75  md:py-0">
            
            <div className="flex h-[70vh] md:h-full ">
                <div className="w-2/3 text-white md:mt-14 flex flex-col md:gap-8 gap-4 px-2 pt-2">
                    <h1 
                        ref={titleRef}
                        className="md:text-4xl text-2xl font-bold opacity-0 translate-y-8 transition-all duration-700 ease-out hover:scale-105 hover:text-blue-600 cursor-pointer"
                    >
                        {t("services.structuring.title")} 
                    </h1>
                    <p 
                        ref={descriptionRef}
                        className="text-sm md:text-base md:w-4/5 opacity-0 translate-y-8 transition-all duration-700 ease-out hover:text-blue-600 cursor-pointer"
                    >
                        {!isMobile ? t("services.structuring.description") : t("services.structuring.description").split(',')[0]}
                    </p>
                </div>
                <div 
                    ref={imageRef}
                    className="w-1/3 rounded-lg overflow-hidden relative h-full opacity-0 translate-y-8 transition-all duration-700 ease-out hover:scale-105 group"
                >
                    
                    <img src="/images/services-structure.jpg" alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 ease-out"/>
                </div>
            </div>
            <div 
                ref={cardsRef}
                className="absolute md:top-[40%] top-[40%] md:rtl:left-[10%] md:ltr:right-[10%] opacity-0 translate-y-8 transition-all duration-700 ease-out hover:scale-105 group"
            >
                <div className="grid grid-cols-3 text-[#244473] bg-[#d9d9d9] text-center rounded-lg md:p-10 p-2 group-hover:bg-gray-200 group-hover:shadow-lg group-hover:shadow-blue-500/20 transition-all duration-300">
                    <div 
                        ref={(el) => { cardRefs.current[0] = el; }}
                        className="rtl:border-l-2 md:rtl:border-l-2 border-[#244473] md:px-2 opacity-0 translate-y-8 transition-all duration-700 ease-out hover:scale-105 hover:bg-blue-100 group cursor-pointer"
                    >
                        <FoundationIcon className="text-[#244473] mb-2 group-hover:text-blue-600 group-hover:rotate-12 transition-all duration-300" sx={{ fontSize: {xs: 30, md: 70} }}/>
                        <p className="md:text-xl text-xs font-bold mb-2 group-hover:text-blue-600 transition-colors duration-300">
                            {!isMobile ? t("services.structuring.cards.card1.title") : t("services.structuring.cards.card1.title").split(',')[0]}
                        </p>
                        <p className="md:text-sm text-xs">
                            {!isMobile ? t("services.structuring.cards.card1.description") : t("services.structuring.cards.card1.description").split(',')[0]}
                        </p>
                    </div>
                    <div 
                        ref={(el) => { cardRefs.current[1] = el; }}
                        className="border-l-2 border-[#244473] md:px-2 opacity-0 translate-y-8 transition-all duration-700 ease-out hover:scale-105 hover:bg-blue-100 group cursor-pointer"
                    >
                        <DesignServicesIcon className="text-[#244473] mb-2 group-hover:text-blue-600 group-hover:rotate-12 transition-all duration-300" sx={{ fontSize: {xs: 30, md: 70} }}/>
                        <p className="md:text-xl text-xs font-bold mb-2 group-hover:text-blue-600 transition-colors duration-300">
                            {!isMobile ? t("services.structuring.cards.card2.title") : t("services.structuring.cards.card2.title").split(',')[0]}
                        </p>
                        <p className="md:text-sm text-xs">
                            {!isMobile ? t("services.structuring.cards.card2.description") : t("services.structuring.cards.card2.description").split(',')[0]}
                        </p>
                    </div>
                    <div 
                        ref={(el) => { cardRefs.current[2] = el; }}
                        className="ltr:border-l-2 border-[#244473] md:px-2 opacity-0 translate-y-8 transition-all duration-700 ease-out hover:scale-105 hover:bg-blue-100 group cursor-pointer"
                    >
                        <LineAxisIcon className="text-[#244473] mb-2 group-hover:text-blue-600 group-hover:rotate-12 transition-all duration-300" sx={{ fontSize: {xs: 30, md: 70} }}/>
                        <p className="md:text-xl text-xs font-bold mb-2 group-hover:text-blue-600 transition-colors duration-300">
                            {!isMobile ? t("services.structuring.cards.card3.title") : t("services.structuring.cards.card3.title").split(',')[0]}
                        </p>
                        <p className="md:text-sm text-xs">
                            {!isMobile ? t("services.structuring.cards.card3.description") : t("services.structuring.cards.card3.description").split(',')[0]}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}